import { useEffect } from "react";
import { Check, Users, Globe, Sparkles } from "lucide-react";
import { LEADER_TIERS, BRAND_ADDON, BRAND_ADDON_PRICE } from "@/data/siteData";

const ICONS = { "in-person-leader": Users, "remote-leader": Globe };
const TIERS = LEADER_TIERS.map((t) => ({ ...t, icon: ICONS[t.id] }));

export default function TechLeadersBrochure() {
  useEffect(() => {
    const shouldPrint =
      new URLSearchParams(window.location.search).get("print") === "1";
    if (shouldPrint) {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="brochure bg-white text-[#111] min-h-screen">
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .brochure { padding: 0 !important; }
          .tier-card { break-inside: avoid; page-break-inside: avoid; }
          .brochure-header { break-after: avoid; }
          .tier-card, .tier-card * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
        .brochure { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        .tier-card-dark {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
      `}</style>

      {/* On-screen toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 bg-[#002FA7] text-white font-display text-xs flex items-center justify-center">
              BoT
            </span>
            <div className="leading-tight">
              <div className="font-display text-sm">BoT Summit 2026 · Brochure</div>
              <div className="overline text-[10px] text-zinc-500">Tech Leaders of Tomorrow</div>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href="/tech-leaders"
              className="text-xs font-bold tracking-[0.2em] uppercase border border-zinc-300 px-4 py-2 hover:bg-black hover:text-white"
            >
              ← Back
            </a>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="print-button"
              className="text-xs font-bold tracking-[0.2em] uppercase bg-[#002FA7] text-white px-4 py-2 hover:bg-black"
            >
              Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable body */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="brochure-header border-b border-zinc-300 pb-8 mb-10">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="font-mono text-[11px] text-[#FF2A00] uppercase tracking-[0.25em] mb-3">
                Tech Leaders of Tomorrow
              </div>
              <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                Recognition
                <br />
                <span className="text-[#002FA7]">Tiers.</span>
              </h1>
              <p className="mt-4 text-sm text-zinc-700 max-w-xl leading-relaxed">
                BoT Summit 2026 honours high-potential operators, founders and CXO's
                as Tech Leaders of Tomorrow — felicitated on the main stage in front
                of 500+ CIOs, CTOs and policymakers.
              </p>
            </div>
            <div className="text-right shrink-0 hidden sm:block print:block">
              <div className="w-14 h-14 bg-[#002FA7] text-white font-display text-sm flex items-center justify-center ml-auto">
                BoT
              </div>
              <div className="mt-3 font-display text-sm leading-tight">BoT Summit 2026</div>
              <div className="overline text-[10px] text-zinc-500 mt-1">Best of Tech</div>
              <div className="mt-3 font-mono text-[11px] text-zinc-600">
                August 2026
                <br />
                Bengaluru
              </div>
            </div>
          </div>
        </div>

        {/* Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-300">
          {TIERS.map((t, i) => {
            const Icon = t.icon;
            return (
              <article
                key={t.id}
                className={`tier-card border-r border-b border-zinc-300 p-8 md:p-10 flex flex-col ${
                  t.highlighted ? "tier-card-dark bg-black text-white" : "bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <Icon
                    size={28}
                    style={{ color: t.highlighted ? "#6E85FF" : t.color }}
                    strokeWidth={1.5}
                  />
                  <span
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
                      t.highlighted ? "text-zinc-400" : "text-zinc-500"
                    }`}
                  >
                    0{i + 1} / 02
                  </span>
                </div>

                <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tighter mt-6">
                  {t.name}
                </h2>
                {t.category && (
                  <div
                    className={`font-mono text-[11px] mt-2 ${
                      t.highlighted ? "text-white/80" : "text-zinc-700"
                    }`}
                  >
                    {t.category}
                  </div>
                )}
                <div
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] mt-2 ${
                    t.highlighted ? "text-zinc-400" : "text-zinc-500"
                  }`}
                >
                  {t.badge}
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-4xl tracking-tighter" style={{ color: t.highlighted ? "#6E85FF" : t.color }}>
                    {t.price}
                  </span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                    {t.priceNote}
                  </span>
                </div>

                <div
                  className={`mt-6 pt-6 border-t ${
                    t.highlighted ? "border-zinc-800" : "border-zinc-200"
                  }`}
                >
                  <div
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-4 ${
                      t.highlighted ? "text-zinc-400" : "text-zinc-500"
                    }`}
                  >
                    Deliverables
                  </div>
                  <ul className="space-y-2.5">
                    {t.deliverables.map((d, k) => (
                      <li key={k} className="flex gap-3 text-[13px] leading-snug">
                        <Check
                          size={14}
                          className="shrink-0 mt-0.5"
                          style={{ color: t.highlighted ? "#6E85FF" : t.color }}
                        />
                        <span
                          className={
                            t.highlighted ? "text-zinc-200" : "text-zinc-700"
                          }
                        >
                          {d}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        <div className="tier-card mt-8 border border-zinc-300 p-8 bg-zinc-50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles size={22} className="text-[#FF2A00]" strokeWidth={1.5} />
              <h2 className="font-display text-2xl uppercase tracking-tighter">Brand add-on</h2>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              +${BRAND_ADDON_PRICE} USD · Optional · In Person tier only
            </span>
          </div>
          <ul className="mt-5 grid md:grid-cols-2 gap-x-8 gap-y-2.5">
            {BRAND_ADDON.map((d) => (
              <li key={d} className="flex gap-3 text-[13px] leading-snug text-zinc-700">
                <Check size={14} className="shrink-0 mt-0.5 text-[#FF2A00]" />
                {d}
              </li>
            ))}
          </ul>
          <p className="mt-5 border-l-4 border-[#FF2A00] bg-[#FF2A00]/10 px-4 py-3 text-[13px] font-semibold leading-snug text-zinc-900">
            Please note that additional charges will apply for reserving on-ground exhibition space for
            product displays or stall setup at the venue.
          </p>
        </div>

        {/* Contact footer */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 border border-zinc-300 p-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Write to us
            </div>
            <div className="font-mono text-sm">info@botsummits.com</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Call / WhatsApp
            </div>
            <div className="font-mono text-sm">+44 7915361665</div>
            <div className="font-mono text-sm">+91 8825035701</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Web
            </div>
            <div className="font-mono text-sm">botsummits.com</div>
          </div>
        </div>

        <div className="mt-6 font-mono text-[11px] text-zinc-600 text-center">
          © Best of Tech Summit — 2026 &nbsp; &nbsp; All fees in USD
        </div>
      </div>
    </div>
  );
}
